import React, { useState, useRef, useEffect } from "react";
import { CalendarDays, ChevronDown, Check } from "lucide-react";

const LeaveRequestDialog = ({ isOpen, onClose, onSubmit }) => {
  const [leaveType, setLeaveType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reason, setReason] = useState("");
  const [leaveDuration, setLeaveDuration] = useState("full-day");
  const [timeSlot, setTimeSlot] = useState({ startTime: "", endTime: "" });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const modalRef = useRef(null);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const leaveOptions = [
    { label: "Casual Leave", count: 2 },
    { label: "Sick Leave", count: 2 },
    { label: "Paid Leave", count: 12 },
    { label: "Compensatory Off", count: 0 },
    { label: "Special Occasion", count: 2 },
    { label: "Emergency Leave", count: 2 },
    { label: "Long term leave", count: 2 },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!leaveType || !fromDate || !toDate) return;
    if (
      leaveDuration === "half-day" &&
      (!timeSlot.startTime || !timeSlot.endTime)
    )
      return;

    // Validate end date > start date
    if (new Date(toDate) < new Date(fromDate)) {
      alert("End date must be after start date");
      return;
    }

    const submitData = {
      leaveType,
      fromDate,
      toDate,
      reason,
      leaveDuration,
      ...(leaveDuration === "half-day" && { timeSlot }),
    };

    onSubmit(submitData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center sm:items-start justify-center z-50 bg-black/40 sm:pt-12 px-4">
      <div
        ref={modalRef}
        className="bg-[#131313] text-white rounded-lg p-4 sm:p-6 w-full max-w-sm sm:max-w-lg shadow-lg border border-[#333333] max-h-[90vh] overflow-y-auto"
      >
        <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
          <CalendarDays
            size={18}
            className="text-[#FFCA20] sm:w-5 sm:h-5"
          />
          Request Leave
        </h2>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          {/* Leave Type Dropdown */}
          <div>
            <label className="block text-xs sm:text-sm mb-1 text-gray-300">
              Leave Type
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full bg-[#131313] border border-[#333333] rounded px-2 sm:px-3 py-2 text-left flex justify-between items-center text-sm"
              >
                <span>{leaveType || "Select Leave Type"}</span>
                <ChevronDown size={14} className="sm:w-4 sm:h-4" />
              </button>

              {dropdownOpen && (
                <div className="absolute z-10 mt-2 w-full bg-[#1e1e1e] border border-[#333333] rounded shadow-lg max-h-48 overflow-y-auto">
                  {leaveOptions.map((option) => (
                    <div
                      key={option.label}
                      onClick={() => {
                        setLeaveType(option.label);
                        setDropdownOpen(false);
                      }}
                      className="px-3 sm:px-4 py-2 hover:bg-[#2a2a2a] flex justify-between items-center cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="leaveType"
                          checked={leaveType === option.label}
                          readOnly
                          className="appearance-none h-4 w-4 sm:h-5 sm:w-5 rounded-full border-2 border-[#444] bg-[#131313] checked:bg-[#FFCA20] checked:border-[#FFCA20] focus:outline-none transition-colors duration-200"
                        />
                        <span className="text-sm">{option.label}</span>
                      </div>
                      <span className="text-xs sm:text-sm text-gray-400">
                        {option.count} left
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Leave Duration */}
          <div>
            <label className="block text-xs sm:text-sm mb-1 text-gray-300">
              Leave Duration
            </label>
            <div className="flex gap-3 sm:gap-4">
              <label className="flex items-center text-sm">
                <input
                  type="radio"
                  name="leaveDuration"
                  value="full-day"
                  checked={leaveDuration === "full-day"}
                  onChange={(e) => setLeaveDuration(e.target.value)}
                  className="mr-2"
                />
                <span>Full Day</span>
              </label>
              <label className="flex items-center text-sm">
                <input
                  type="radio"
                  name="leaveDuration"
                  value="half-day"
                  checked={leaveDuration === "half-day"}
                  onChange={(e) => setLeaveDuration(e.target.value)}
                  className="mr-2"
                />
                <span>Half Day</span>
              </label>
            </div>
          </div>

          {/* Time Slot (only for half-day) */}
          {leaveDuration === "half-day" && (
            <div>
              <label className="block text-xs sm:text-sm mb-1 text-gray-300">
                Time Slot
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="w-full sm:w-1/2">
                  <input
                    type="time"
                    value={timeSlot.startTime}
                    onChange={(e) =>
                      setTimeSlot((prev) => ({
                        ...prev,
                        startTime: e.target.value,
                      }))
                    }
                    className="w-full bg-[#131313] border border-[#333333] rounded px-2 sm:px-3 py-2 text-white text-sm"
                    placeholder="Start Time"
                  />
                </div>
                <div className="w-full sm:w-1/2">
                  <input
                    type="time"
                    value={timeSlot.endTime}
                    onChange={(e) =>
                      setTimeSlot((prev) => ({
                        ...prev,
                        endTime: e.target.value,
                      }))
                    }
                    className="w-full bg-[#131313] border border-[#333333] rounded px-2 sm:px-3 py-2 text-white text-sm"
                    placeholder="End Time"
                  />
                </div>
              </div>
            </div>
          )}

          {/* From and To Dates */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="w-full sm:w-1/2">
              <label className="block text-xs sm:text-sm mb-1 text-gray-300">
                From Date
              </label>
              <div
                onClick={() =>
                  fromDateRef.current?.showPicker?.() ||
                  fromDateRef.current?.focus()
                }
                className="w-full bg-[#131313] border border-[#333333] rounded px-2 sm:px-3 py-2 text-white text-sm cursor-pointer"
              >
                <input
                  ref={fromDateRef}
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full bg-transparent border-none outline-none text-white text-sm cursor-pointer"
                />
              </div>
            </div>
            <div className="w-full sm:w-1/2">
              <label className="block text-xs sm:text-sm mb-1 text-gray-300">
                To Date
              </label>
              <div
                onClick={() =>
                  toDateRef.current?.showPicker?.() || toDateRef.current?.focus()
                }
                className="w-full bg-[#131313] border border-[#333333] rounded px-2 sm:px-3 py-2 text-white text-sm cursor-pointer"
              >
                <input
                  ref={toDateRef}
                  type="date"
                  value={toDate}
                  min={fromDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full bg-transparent border-none outline-none text-white text-sm cursor-pointer"
                />
              </div>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-xs sm:text-sm mb-1 text-gray-300">
              Reason for Leave
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for taking leave"
              className="w-full bg-[#131313] border border-[#333333] rounded px-2 sm:px-3 py-2 text-white text-sm"
            />
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-0 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-3 sm:px-4 py-2 border-2 border-white text-white rounded hover:bg-gray-800 transition text-sm order-2 sm:order-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 sm:px-4 py-2 bg-[#FFCA20] text-black border-2 border-yellow-500 rounded hover:bg-yellow-300 transition flex items-center justify-center gap-2 text-sm order-1 sm:order-2"
            >
              Submit Request <Check size={14} className="sm:w-4 sm:h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeaveRequestDialog;
